import React from 'react';
import { File, Files, X, Loader } from 'lucide-react';

interface FileChipProps {
  /** Number of files selected */
  fileCount: number;
  /** Name of the first/primary file */
  fileName?: string;
  /** Click handler for the chip */
  onClick?: () => void;
  /** Click handler for the X button (clear files) */
  onClear?: (e: React.MouseEvent) => void;
  /** Whether to show hover effect on X */
  showClearOnHover?: boolean;
  /** Whether files are currently loading */
  isLoading?: boolean;
}

/**
 * File selection chip component used in chat inputs
 * Shows file icon, name, and count with optional clear button
 *
 * @example
 * <FileChip
 *   fileCount={3}
 *   fileName="video.mp4"
 *   onClick={() => setShowFileList(true)}
 *   onClear={() => setSelectedFiles([])}
 * />
 */
export const FileChip: React.FC<FileChipProps> = ({
  fileCount,
  fileName = 'Files',
  onClick,
  onClear,
  showClearOnHover = true,
  isLoading = false,
}) => {
  const getDisplayText = () => {
    if (isLoading) {
      // If fileName is provided during loading (e.g., "Searching"), use it
      if (fileName && fileName !== 'Files') {
        return fileName;
      }
      return fileCount === 1 ? 'File' : `${fileCount} files`;
    }
    return fileName;
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex items-center gap-1.5 h-7 bg-gray-100 dark:bg-secondary hover:bg-gray-200 dark:hover:bg-secondary/80 text-foreground border border-gray-300 dark:border-border rounded-full px-2.5 transition-colors"
    >
      <div className="relative w-3 h-3 text-gray-400 dark:text-gray-500">
        {isLoading ? (
          <Loader
            className="h-3 w-3 absolute inset-0 animate-spin"
            strokeWidth={2}
          />
        ) : (
          <>
            {fileCount === 1 ? (
              <File
                className={`h-3 w-3 absolute inset-0 transition-opacity ${
                  showClearOnHover ? 'group-hover:opacity-0' : ''
                }`}
                strokeWidth={2}
              />
            ) : (
              <Files
                className={`h-3 w-3 absolute inset-0 transition-opacity ${
                  showClearOnHover ? 'group-hover:opacity-0' : ''
                }`}
                strokeWidth={2}
              />
            )}
            {showClearOnHover && onClear && (
              <X
                className="h-3 w-3 absolute inset-0 transition-opacity opacity-0 group-hover:opacity-100"
                strokeWidth={2}
                onClick={(e) => {
                  e.stopPropagation();
                  onClear(e);
                }}
              />
            )}
          </>
        )}
      </div>
      <span className="text-xs font-medium truncate max-w-[150px]">
        {getDisplayText()}
      </span>
      {!isLoading && fileCount > 1 && (
        <span className="text-xs font-medium">+{fileCount - 1}</span>
      )}
    </button>
  );
};
